// Full 78-card Rider-Waite-Smith deck data.
// Card images sourced from Wikimedia Commons (public domain).
const wm = (file) => `https://commons.wikimedia.org/wiki/Special:FilePath/${encodeURIComponent(file)}?width=500`;

// ---------- MAJOR ARCANA (00 - 21) ----------
const MAJORS_RAW = [
  {
    n: 0, name: "The Fool", file: "RWS_Tarot_00_Fool.jpg",
    element: "Air · Uranus", keywords: ["beginnings", "innocence", "spontaneity", "leap of faith"],
    description: "A young traveller steps off a cliff in radiant innocence, white rose in hand, a faithful dog at his heels. He carries little but trusts everything — the open sky behind him, the unknown valley below.",
    upright: "A fresh start, a leap of faith, openness to the unknown. Trust the call, take the first step, embrace the adventure.",
    reversed: "Recklessness, naivety, holding back from a needed risk. Look before you leap — but don't refuse the journey.",
  },
  {
    n: 1, name: "The Magician", file: "RWS_Tarot_01_Magician.jpg",
    element: "Air · Mercury", keywords: ["manifestation", "willpower", "skill", "focus"],
    description: "A figure stands before an altar bearing wand, cup, sword and pentacle — all four elements. One hand reaches up to spirit, the other points down to earth. Above his head floats the infinity symbol.",
    upright: "You have everything you need to create. Set your intention, gather your tools, and act with focused will.",
    reversed: "Scattered energy, manipulation, untapped potential. Re-align your motives and use your gifts wisely.",
  },
  {
    n: 2, name: "The High Priestess", file: "RWS_Tarot_02_High_Priestess.jpg",
    element: "Water · Moon", keywords: ["intuition", "mystery", "inner voice", "the unconscious"],
    description: "She sits between two pillars — black and white — a crescent moon at her feet and a scroll of hidden knowledge in her lap. The veil behind her is woven with pomegranates.",
    upright: "Listen inward. Sacred knowledge is available through stillness, dreams, and intuition. Trust what you sense.",
    reversed: "Disconnection from intuition, secrets surfacing, intuition ignored. Return to silence and ask again.",
  },
  {
    n: 3, name: "The Empress", file: "RWS_Tarot_03_Empress.jpg",
    element: "Earth · Venus", keywords: ["abundance", "nurture", "fertility", "creativity"],
    description: "The Empress reclines in a field of golden wheat, surrounded by lush nature. A crown of twelve stars adorns her head, representing the zodiac. She embodies the Mother archetype — fertile, abundant, and deeply connected to the earth.",
    upright: "A period of abundance, creativity, and sensual pleasure. Nurture your projects and relationships — they are ready to flourish.",
    reversed: "Creative block, dependence, neglect. You may be smothering or neglecting what needs your care. Find balance in giving and receiving.",
  },
  {
    n: 4, name: "The Emperor", file: "RWS_Tarot_04_Emperor.jpg",
    element: "Fire · Aries", keywords: ["authority", "structure", "stability", "leadership"],
    description: "A sovereign sits on a stone throne carved with rams, holding an ankh and orb. The barren mountains behind him speak of discipline forged through trial.",
    upright: "Step into your authority. Bring structure to your world, lead with steady resolve, claim your role.",
    reversed: "Rigidity, domineering control, immaturity. Loosen the grip — power without compassion crumbles.",
  },
  {
    n: 5, name: "The Hierophant", file: "RWS_Tarot_05_Hierophant.jpg",
    element: "Earth · Taurus", keywords: ["tradition", "teaching", "spiritual wisdom", "belonging"],
    description: "A robed teacher sits between two pillars, raising one hand in blessing. Two acolytes kneel before him, and the crossed keys at his feet unlock the mysteries.",
    upright: "A mentor, sacred tradition, or shared path. Honour the wisdom passed down — there is power in the lineage.",
    reversed: "Question authority, break with convention, or find your own truth beyond the institution.",
  },
  {
    n: 6, name: "The Lovers", file: "RWS_Tarot_06_Lovers.jpg",
    element: "Air · Gemini", keywords: ["love", "union", "values", "choice"],
    description: "A man and woman stand beneath a great angel, the sun blazing behind. Behind her, the Tree of Knowledge; behind him, the Tree of Life. A sacred choice unfolds.",
    upright: "Deep harmony, soulful union, alignment with what you value. A meaningful choice made from love.",
    reversed: "Misalignment, indecision, broken trust. Look honestly at what — and who — you truly value.",
  },
  {
    n: 7, name: "The Chariot", file: "RWS_Tarot_07_Chariot.jpg",
    element: "Water · Cancer", keywords: ["determination", "victory", "willpower", "momentum"],
    description: "A warrior stands in a chariot drawn by two sphinxes — one black, one white. The starry canopy above him shows the cosmos itself moves with his intent.",
    upright: "Focused drive carries you to victory. Hold the reins of opposing forces and steer ahead with confidence.",
    reversed: "Loss of direction, scattered effort, force without focus. Pause, realign, then move.",
  },
  {
    n: 8, name: "Strength", file: "RWS_Tarot_08_Strength.jpg",
    element: "Fire · Leo", keywords: ["courage", "compassion", "inner power", "patience"],
    description: "A woman gently closes the jaws of a lion with quiet hands and a calm gaze. Above her head floats the infinity symbol — power without force.",
    upright: "Lead with heart. Inner courage and compassion tame what fear cannot. You are stronger than you think.",
    reversed: "Self-doubt, suppressed emotion, force misused. Return to gentleness — softness is your real power.",
  },
  {
    n: 9, name: "The Hermit", file: "RWS_Tarot_09_Hermit.jpg",
    element: "Earth · Virgo", keywords: ["introspection", "soul-searching", "inner light", "guidance"],
    description: "A cloaked elder stands alone on a snowy peak, lantern held high — its glow contains a six-pointed star. He has found wisdom in solitude.",
    upright: "Withdraw to listen. Truth lives in the quiet places — your own inner light will guide you.",
    reversed: "Isolation, withdrawal, refusing wisdom. Rejoin the world — or take the solitude you actually need.",
  },
  {
    n: 10, name: "Wheel of Fortune", file: "RWS_Tarot_10_Wheel_of_Fortune.jpg",
    element: "Fire · Jupiter", keywords: ["cycles", "destiny", "change", "turning points"],
    description: "A great wheel spins in the sky, surrounded by the four living creatures — eagle, lion, ox, angel — each studying sacred books. Fate is in motion.",
    upright: "A turning point. Fortune turns in your favour — flow with the cycle, trust the timing.",
    reversed: "Resistance to change, bad luck, cycles repeating. Look for the lesson the wheel keeps offering.",
  },
  {
    n: 11, name: "Justice", file: "RWS_Tarot_11_Justice.jpg",
    element: "Air · Libra", keywords: ["truth", "fairness", "karma", "clear judgement"],
    description: "A robed figure sits between two pillars, holding sword aloft and scales steady. Every action is weighed; every truth honoured.",
    upright: "Truth comes to light. Act with integrity — what you give returns in kind.",
    reversed: "Unfairness, dishonesty, avoiding accountability. Own your part. Balance restores itself.",
  },
  {
    n: 12, name: "The Hanged Man", file: "RWS_Tarot_12_Hanged_Man.jpg",
    element: "Water · Neptune", keywords: ["surrender", "new perspective", "pause", "letting go"],
    description: "A man hangs upside-down from a living tree, calm and haloed. Voluntary surrender has flipped his view of the world.",
    upright: "Pause. Surrender the struggle and let a new perspective arrive. Sometimes stillness is the answer.",
    reversed: "Stalling, martyrdom, fear of change. The pause is over — let go and move.",
  },
  {
    n: 13, name: "Death", file: "RWS_Tarot_13_Death.jpg",
    element: "Water · Scorpio", keywords: ["endings", "transformation", "rebirth", "release"],
    description: "A skeletal figure on a white horse rides under a banner of the white rose. Behind it, the sun rises between two towers — death is the threshold of new life.",
    upright: "An ending makes way for something true. Release what is finished — transformation is already underway.",
    reversed: "Resistance to change, fear of letting go, stagnation. The longer you cling, the harder the lesson.",
  },
  {
    n: 14, name: "Temperance", file: "RWS_Tarot_14_Temperance.jpg",
    element: "Fire · Sagittarius", keywords: ["balance", "patience", "moderation", "alchemy"],
    description: "An angel pours water between two cups in an impossible stream — one foot on land, one in the pool. Gentle alchemy in motion.",
    upright: "Patience and gentle blending. Bring opposites into harmony — the middle way is the way.",
    reversed: "Imbalance, excess, impatience. Slow down and find your centre again.",
  },
  {
    n: 15, name: "The Devil", file: "RWS_Tarot_15_Devil.jpg",
    element: "Earth · Capricorn", keywords: ["shadow", "attachment", "bondage", "liberation"],
    description: "A horned figure looms above two chained lovers — yet the chains around their necks are loose. The bondage is largely chosen.",
    upright: "Look at what binds you. Unhealthy patterns, fears, or addictions are loud — and looser than they feel.",
    reversed: "Awakening. You see the chain, and you can choose to step out. Freedom is closer than you think.",
  },
  {
    n: 16, name: "The Tower", file: "RWS_Tarot_16_Tower.jpg",
    element: "Fire · Mars", keywords: ["upheaval", "revelation", "breakthrough", "sudden change"],
    description: "Lightning strikes a great tower; flames burst forth as crown and figures fall to earth. False structures collapse in a flash of truth.",
    upright: "A sudden shake-up clears illusion. What falls was never built on truth — make space for what's real.",
    reversed: "Delaying the inevitable, fearing the necessary change. The shake-up is coming — softer if you allow it.",
  },
  {
    n: 17, name: "The Star", file: "RWS_Tarot_17_Star.jpg",
    element: "Air · Aquarius", keywords: ["hope", "renewal", "serenity", "inspiration"],
    description: "A naked woman kneels beside a pool, pouring water onto land and into the water. Above her, seven small stars and one great star shine — the universe blesses her.",
    upright: "Hope returns. Heal, replenish, and trust the gentle current — your guiding star is steady.",
    reversed: "Despondency, faith lost, disconnection. Look up — the star is still there, even when you can't feel it.",
  },
  {
    n: 18, name: "The Moon", file: "RWS_Tarot_18_Moon.jpg",
    element: "Water · Pisces", keywords: ["dreams", "intuition", "illusion", "the unconscious"],
    description: "A great moon hangs over a winding path between two towers. A wolf and a dog howl; a crayfish climbs from the depths. The world of dream and shadow.",
    upright: "Trust your gut, even when nothing is clear. Honour the dreams — they're speaking.",
    reversed: "Illusion lifts, fear releases, confusion clears. Truth surfaces.",
  },
  {
    n: 19, name: "The Sun", file: "RWS_Tarot_19_Sun.jpg",
    element: "Fire · Sun", keywords: ["joy", "vitality", "success", "clarity"],
    description: "A radiant child rides a white horse beneath a great sun, banner streaming, a wall of sunflowers blooming behind. Pure, undefended joy.",
    upright: "Joy, success, warmth. Let your light out — you are seen, and you are enough.",
    reversed: "Joy delayed but not denied. Reconnect with simple pleasures and inner sunlight.",
  },
  {
    n: 20, name: "Judgement", file: "RWS_Tarot_20_Judgement.jpg",
    element: "Fire · Pluto", keywords: ["awakening", "calling", "renewal", "reckoning"],
    description: "An angel sounds a trumpet above figures rising from their tombs, arms open in welcome. A call has come; a new life begins.",
    upright: "A spiritual awakening. Hear the call, forgive the past, and step into who you were always becoming.",
    reversed: "Ignoring the call, self-judgement, hesitation. The trumpet keeps sounding. Listen.",
  },
  {
    n: 21, name: "The World", file: "RWS_Tarot_21_World.jpg",
    element: "Earth · Saturn", keywords: ["completion", "wholeness", "fulfilment", "integration"],
    description: "A dancer wreathed in a green laurel completes the cosmic dance, surrounded by the four living creatures of the elements. The journey is whole.",
    upright: "Completion. A cycle closes in fulfilment — celebrate, integrate, and prepare for the next dance.",
    reversed: "Incompletion, loose ends, almost-there. Tie up what remains; the closure is at hand.",
  },
];

// ---------- MINOR ARCANA generators ----------
const SUITS = {
  wands:    { element: "Fire",  theme: "passion, drive, dreams and aspirations",   fileBase: "Wands"  },
  cups:     { element: "Water", theme: "love, emotion, feeling and flow",          fileBase: "Cups"   },
  swords:   { element: "Air",   theme: "thought, mind, truth and conflict",        fileBase: "Swords" },
  pentacles:{ element: "Earth", theme: "body, money, work and the material world", fileBase: "Pents"  },
};

// 10 numbered + 4 court entries per suit. n: 1..14.
// Concise meanings per pip — each card gets a tailored description below.
const PIP_TEMPLATE = {
  1:  { name: "Ace",       up: "A new beginning in this suit — a pure spark of possibility offered to you.", rv: "A blocked beginning or wasted potential. Look at what stops you from accepting the gift.", kw: ["beginning","gift","seed","potential"] },
  2:  { name: "Two",       up: "A choice or partnership — two energies meeting and weighing each other.", rv: "Imbalance or indecision. One side has grown too heavy; recalibrate.", kw: ["choice","balance","partnership"] },
  3:  { name: "Three",     up: "Growth and creative expansion through cooperation.", rv: "Plans delayed, miscommunication, growth that stalls.", kw: ["growth","collaboration","expansion"] },
  4:  { name: "Four",      up: "Stability — a moment of structure and rest.", rv: "Stagnation, possessiveness, or boredom in the structure.", kw: ["stability","rest","structure"] },
  5:  { name: "Five",      up: "Conflict or loss — the suit's challenging midpoint.", rv: "Recovery from conflict, lessons received, healing begins.", kw: ["challenge","loss","tension"] },
  6:  { name: "Six",       up: "Harmony restored — movement, generosity, return to ease.", rv: "Old patterns re-emerging, lingering nostalgia.", kw: ["harmony","movement","relief"] },
  7:  { name: "Seven",     up: "A test of will or perseverance — defend what matters.", rv: "Surrender, retreat, or rethinking your stance.", kw: ["test","strategy","perseverance"] },
  8:  { name: "Eight",     up: "Mastery through focus and consistent effort.", rv: "Burn-out, scattered focus, or rushing the craft.", kw: ["mastery","focus","effort"] },
  9:  { name: "Nine",      up: "Near-completion — a deep dive into the suit's gifts.", rv: "Excess or guarding what doesn't need guarding.", kw: ["depth","near-completion","fullness"] },
  10: { name: "Ten",       up: "Completion of a cycle in this suit — for better or for fuller.", rv: "Burden, overload, or a cycle that needs a clean ending.", kw: ["completion","cycle","fulfilment"] },
  11: { name: "Page",      up: "A messenger of the suit — youthful curiosity, news, or a fresh student energy.", rv: "Immaturity, gossip, or a message ignored.", kw: ["messenger","student","news"] },
  12: { name: "Knight",    up: "Bold movement in the suit's direction — action, quests, momentum.", rv: "Recklessness or being stuck in the saddle.", kw: ["action","movement","quest"] },
  13: { name: "Queen",     up: "Mastery of the suit's inner world — grace, depth, embodied wisdom.", rv: "Energy out of balance — too cold, too clinging, or shadow expressed.", kw: ["embodiment","grace","wisdom"] },
  14: { name: "King",      up: "Mastery of the suit's outer world — authority, vision, mature leadership.", rv: "Tyranny, rigidity, or power used for ego.", kw: ["authority","leadership","mastery"] },
};

// Suit-specific colour / per-card descriptions (kept short).
const SUIT_DESCRIPTIONS = {
  wands: {
    1:"A hand emerges from a cloud holding a flowering wand — pure creative fire offered freely.",
    2:"A figure holds a globe between two wands, gazing toward distant possibility.",
    3:"A merchant looks out to sea while his ships return — early success and broader vision.",
    4:"Two figures dance beneath a wreathed canopy of wands — celebration and homecoming.",
    5:"Five young men spar with wands — friction, competition, scrappy energy.",
    6:"A laurel-crowned rider parades home triumphant on a white horse.",
    7:"A figure on high ground defends his wands against unseen challengers below.",
    8:"Eight wands fly through the sky in unison — momentum, speed, swift news.",
    9:"A wounded figure leans on his wand, guarding eight more — battle-weary but vigilant.",
    10:"A man carries ten wands awkwardly — the weight of success and what it asks of you.",
    11:"A youth holds a sprouting wand, full of curiosity for the journey ahead.",
    12:"A knight in flaming armour gallops forward, all forward momentum.",
    13:"A queen sits on a throne flanked by lions, sunflower in hand, cat at her feet — warm, charismatic, sovereign.",
    14:"A king sits on a fiery throne, salamander at his feet — visionary, courageous, commanding.",
  },
  cups: {
    1:"A hand offers a chalice from which five streams of water flow — pure emotional grace.",
    2:"A couple exchanges cups beneath the caduceus of Hermes — a sacred meeting of hearts.",
    3:"Three women raise their cups in a circle of friendship and celebration.",
    4:"A figure broods beneath a tree while a hand offers a fourth cup from a cloud.",
    5:"A cloaked figure mourns three spilled cups, unaware that two remain upright behind.",
    6:"A child offers a flower-filled cup to another in a sunny courtyard — innocence and nostalgia.",
    7:"A figure faces seven dreamlike cups in the clouds — visions, fantasies, choice.",
    8:"A traveller turns from eight stacked cups, walking toward distant mountains under the moon.",
    9:"A satisfied man sits with arms folded before nine cups in an arch — emotional contentment.",
    10:"A family rejoices beneath ten cups arched as a rainbow — emotional fulfilment.",
    11:"A page contemplates a fish leaping from his cup — playful intuition.",
    12:"A knight rides slowly forward, cup raised — a romantic invitation, a quiet quest.",
    13:"A queen gazes into an ornate covered cup — deeply empathic, intuitive, soulful.",
    14:"A king sits steady on a sea-throne — emotional mastery, diplomacy, kind authority.",
  },
  swords: {
    1:"A hand grips a single sword crowned with laurel — clarity, breakthrough, a single piercing truth.",
    2:"A blindfolded figure crosses two swords over her chest by the sea — a stalemate, decision deferred.",
    3:"A heart is pierced by three swords beneath grey clouds — sorrow, heartbreak, painful truth.",
    4:"A knight lies in repose atop a tomb, three swords above and one beneath — rest, recuperation.",
    5:"A figure gathers swords as two walk away defeated — hollow victory.",
    6:"A ferryman poles a boat with six swords across calm water — leaving turbulence behind.",
    7:"A figure tiptoes from camp with five swords — strategy, stealth, or self-deception.",
    8:"A blindfolded woman stands bound between eight swords — yet the way out is open.",
    9:"A figure sits up in bed clutching her head, nine swords on the wall behind — anxiety, night fears.",
    10:"A figure lies face-down beneath ten swords on the shore — rock-bottom, the dawn rising.",
    11:"A youth stands with sword raised against a stormy sky — vigilant curiosity, keen intellect.",
    12:"A knight charges into the wind, sword aloft — fierce ideas, swift change.",
    13:"A queen sits crowned with cherubs and clouds, sword upright — clear-eyed, honest, decisive.",
    14:"A king sits on a throne of butterflies, sword upright — disciplined mind, fair authority.",
  },
  pentacles: {
    1:"A hand offers a great golden coin above a flowering garden — a tangible new opportunity.",
    2:"A juggler dances with two coins linked by an infinity symbol — balancing two demands.",
    3:"An artisan works on a cathedral, consulting two onlookers — craft and collaboration.",
    4:"A miser clutches four coins — security clutched too tightly.",
    5:"Two beggars trudge past a stained-glass window in the snow — outside warmth they could enter.",
    6:"A merchant weighs gold and offers coins to two kneeling figures — generosity in balance.",
    7:"A farmer leans on his hoe surveying his slow-growing crop — patient assessment.",
    8:"An apprentice carves coin after coin with focused hands — diligent mastery.",
    9:"A noblewoman walks alone in a vineyard with a hooded falcon — self-made abundance.",
    10:"A patriarch with two dogs watches generations gather beneath an archway — legacy, family wealth.",
    11:"A youth gazes at a coin held high — studious, devoted, a slow but sincere start.",
    12:"A knight sits motionless on his horse, coin held — patient, reliable, methodical.",
    13:"A queen gazes at the coin in her lap, surrounded by garden growth — earthy, nurturing, generous.",
    14:"A king sits among grapes and bulls, coin steady on his knee — prosperous, grounded, secure.",
  },
};

const buildSuit = (suitKey) => {
  const s = SUITS[suitKey];
  const cards = [];
  for (let n = 1; n <= 14; n++) {
    const pip = PIP_TEMPLATE[n];
    const fileNum = String(n).padStart(2, "0");
    cards.push({
      id: `${suitKey}-${n}`,
      arcana: "minor",
      suit: suitKey,
      n,
      label: pip.name,
      name: `${pip.name} of ${suitKey.charAt(0).toUpperCase()}${suitKey.slice(1)}`,
      element: s.element,
      theme: s.theme,
      keywords: pip.kw,
      description: SUIT_DESCRIPTIONS[suitKey][n],
      upright: pip.up,
      reversed: pip.rv,
      imageUrl: wm(`${s.fileBase}${fileNum}.jpg`),
    });
  }
  return cards;
};

export const MAJOR_ARCANA = MAJORS_RAW.map((m) => ({
  id: `major-${m.n}`,
  arcana: "major",
  suit: null,
  n: m.n,
  label: String(m.n).padStart(2, "0"),
  name: m.name,
  element: m.element,
  keywords: m.keywords,
  description: m.description,
  upright: m.upright,
  reversed: m.reversed,
  imageUrl: wm(m.file),
}));

export const WANDS = buildSuit("wands");
export const CUPS = buildSuit("cups");
export const SWORDS = buildSuit("swords");
export const PENTACLES = buildSuit("pentacles");

export const DECK = [...MAJOR_ARCANA, ...WANDS, ...CUPS, ...SWORDS, ...PENTACLES];

export const SUIT_INFO = [
  { key: "wands",     label: "Wands",     element: "Fire",  theme: "Dreams & aspirations", cards: WANDS },
  { key: "cups",      label: "Cups",      element: "Water", theme: "Flow & emotions",      cards: CUPS },
  { key: "swords",    label: "Swords",    element: "Air",   theme: "Thinking & the mind",  cards: SWORDS },
  { key: "pentacles", label: "Pentacles", element: "Earth", theme: "Money & material",     cards: PENTACLES },
];
